/**
 * Shared queries used by the shell and the UI kit. Module helpers keep their own queries
 * next to their pages but reuse EP (./endpoints) and the Page<T> shape. Query keys start
 * with the org id so switching organisation never shows another tenant's cached rows.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import { EP, type Res } from './endpoints';

export type PublicSettings = Res<'GET /settings/public'>;

export const usePublicSettings = () =>
  useQuery({ queryKey: ['settings', 'public'], queryFn: () => api<PublicSettings>(EP.settingsPublic, { anonymous: true }), staleTime: 10 * 60_000, retry: false });

/** Key prefix for anything tenant-scoped. */
export function useOrgKey(): string {
  const { user } = useAuth();
  return user?.org?.id ?? 'none';
}

const list = <T,>(r: T[] | { data: T[] }): T[] => (Array.isArray(r) ? r : r.data);

export function useCabinets() {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({ queryKey: [org, 'cabinets'], enabled: !!user?.org, queryFn: () => api<Res<'GET /cabinets'>>(EP.cabinets).then(list), staleTime: 60_000 });
}

export function useFolders(cabinetId: string, parentId: string | null, enabled = true) {
  const org = useOrgKey();
  return useQuery({
    queryKey: [org, 'folders', cabinetId, parentId ?? 'root'],
    enabled,
    queryFn: () => api<Res<'GET /cabinets/:id/folders'>>(EP.folders(cabinetId), { query: { parentId: parentId ?? undefined } }).then(list),
    staleTime: 60_000,
  });
}

export function useDocumentTypes() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'document-types'], queryFn: () => api<Res<'GET /document-types'>>(EP.documentTypes).then(list), staleTime: 5 * 60_000 });
}

export function useTags() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'tags'], queryFn: () => api<Res<'GET /tags'>>(EP.tags).then(list), staleTime: 5 * 60_000 });
}

export function useCorrespondents() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'correspondents'], queryFn: () => api<Res<'GET /correspondents'>>(EP.correspondents).then(list), staleTime: 5 * 60_000 });
}

export function useOrgUsers(enabled = true) {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'users', 'refs'], enabled, queryFn: () => api<Res<'GET /users/directory'>>(EP.usersDirectory).then((rows) => rows.map((u) => ({ id: u.id, name: u.name }))), staleTime: 5 * 60_000, retry: false });
}

/** Processing queue for the top-bar indicator, polled every 15 s while anything is in flight. */
export function useQueueSummary() {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({
    queryKey: [org, 'queue'],
    enabled: !!user?.org,
    queryFn: () => api<Res<'GET /queue/summary'>>(EP.queue, { background: true }),
    refetchInterval: (q) => (q.state.data && q.state.data.queued + q.state.data.running > 0 ? 15_000 : 60_000),
    retry: false,
  });
}

export function useNotifications() {
  const org = useOrgKey();
  const { user } = useAuth();
  return useQuery({
    queryKey: [org, 'notifications'],
    enabled: !!user,
    queryFn: () => api<Res<'GET /notifications'>>(EP.notifications, { query: { pageSize: 20 }, background: true }).then(list),
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useHomeSummary() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'home'], queryFn: () => api<Res<'GET /dashboard/home'>>(EP.home, { background: true }), refetchInterval: 60_000, retry: false });
}

export function useRecentSearches() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'searches', 'recent'], queryFn: () => api<Res<'GET /searches/recent'>>(EP.searches.recent).then(list), retry: false });
}

export function useSavedSearches() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'searches', 'saved'], queryFn: () => api<Res<'GET /searches/saved'>>(EP.searches.saved).then(list), retry: false });
}

export function useRecentlyOpened() {
  const org = useOrgKey();
  return useQuery({ queryKey: [org, 'documents', 'recently-opened'], queryFn: () => api<Res<'GET /documents/recently-opened'>>(EP.documents.recentlyOpened).then(list), retry: false });
}
